import { CanvasEvent } from '@core/helpers/coreEnums';
import { resolveDuplicateFabricIds } from '@core/helpers/resolveDuplicateFabricIds.js';
import Runtime from '@core/helpers/runtime.js';
import { addNode } from '@core/libs/objects/pipes/nodes/add';
import { moveNodeWithController } from '@core/libs/objects/pipes/nodes/moveNodeWithController';
import clearAllPipes from '@core/libs/objects/pipes/pipes/clearAll';
import { drawAllPipes, drawPipe } from '@core/libs/objects/pipes/pipes/draw';
import finishPipe from '@core/libs/objects/pipes/pipes/finish';
import { registerPipeCanvasEvents } from '@core/libs/objects/pipes/pipes/registerEvents';
import SprinklerTools from '@core/libs/objects/sprinklers/shared/sprinklertools.js';
import { initSprinklerStackIconObjects } from '@core/libs/objects/sprinklers/stacks/sprinklerStackObject.js';
import { rescaleWaterConnectors } from '@core/libs/objects/water-connectors/rescaleWaterConnectors.js';
import { rescaleWaterSupply } from '@core/libs/objects/water-outlet/updateWaterSupply.js';
import ReduxStore from '@core/libs/store/reduxStore.js';
// core dependencies
import { resetStore } from '@core/libs/store/resetStore.js';
import { setModals } from '@core/libs/store/slices/Gui.slice.js';
import UserApi from '@core/server/userApi.js';
import { Layer } from '@sharedGlobal/helpers/enums/layer.js';
import { slugIndex } from '@sharedGlobal/helpers/slugIndex.js';
// shared dependencies
import i18next from 'i18next';
import { isUndefined } from 'lodash-es';

import { initReactComponents } from '../../components/initReactComponents.jsx';
import { enableDisableSubmenus } from '../../gui-modes/components/object-menu/helpers/closeMenuItems.js';
import {
  addGuideWireIntersectionNotices,
  clearGuideWireIntersectionNotices,
} from '../../gui-modes/robotic-mower/3_manual_editing/guideWireIntersectionNotice.js';
import irrigationController from '../../gui-modes/shared/irrigation-controller/irrigationController.jsx';
import { executePipelineAutomation } from '../../gui-modes/shared/objects/executePipelineAutomation.js';
import {
  showModalDifferentWaterConnectorTypes,
  showModalTooManyWaterConnectors,
} from '../../gui-modes/water-connectors/waterConnectorModals.jsx';
import { removePressureVisualization } from '../../libs/objects/water-outlet/removePressureVisualization.js';
import { updatePressureVisualization } from '../../libs/objects/water-outlet/updatePressureVisualization.js';
import { updateWaterOutletIcon } from '../../libs/objects/water-outlet/updateWaterOutletIcon.js';
import { setForceUpdateMenu, setMenuData, setObjectStates, setPlanMode, setZoom } from '../../libs/store/slices/MontanaGui.slice.js';
import { setControllerData } from '../../libs/store/slices/MontanaIrrigation.slice.js';
import { showWaterOutletErrorModal } from '../../shared/components/water-outlet/showWaterOutletErrorModal.js';
import { modalMessages, MontanaMode } from '../../shared/helpers/montanaEnums.js';
import { MontanaEventBus, MontanaEvents } from '../../shared/helpers/montanaEventBus.js';
import MontanaAPI from '../../shared/libs/montanaAPI.js';
import MontanaIrrigationAPI from '../../shared/libs/montanaIrrigationAPI.js';
import initResponsiveTools from './canvas-interaction/responsiveTools.js';
import initGardenPlanner from './initGardenPlanner.js';
import regionSwitcherEvents from './menus/regionSwitcher.js';
import { startGardenMode } from './menus/startGardenMode.js';
import { newGardenSaveBefore } from './newGardenSaveBefore.js';
import { montanaSave, montanaSaveGardenAs } from './saveGarden.js';
import { initStartOverlay } from './startOverlay.js';

const newGardenHandler = () => {
  MontanaAPI.view.scaleCanvas(0);
  MontanaEventBus.emit(MontanaEvents.gui.pipelines.reset, true);
  resetStore();
  startGardenMode();
};

const dropWaterOutletHandler = (objectToAdd) => {
  if (!objectToAdd || objectToAdd.slug !== 'water-outlet') {
    return;
  }
  objectToAdd.set({
    lockMovementX: true,
    lockMovementY: true,
    locked: true,
  });

  if (MontanaAPI.objects.getBySlug('water-outlet').length === 1) {
    ReduxStore.dispatch(setModals({ activeModal: modalMessages.WaterOutlet.Dropped }));
  } else {
    const activeObject = MontanaAPI.getCanvas().getActiveObject();

    MontanaAPI.getCanvas().remove(activeObject);
    showWaterOutletErrorModal();
  }

  MontanaIrrigationAPI.waterSupply.update(MontanaIrrigationAPI.waterSupply.getMain());
};

const objectSelectedHandler = (object) => {
  if (object.layer === Layer.Sprinklers) {
    SprinklerTools.reInit(object);
  }
};

const objectMoveStartHandler = (object) => {
  if (isUndefined(object)) {
    return;
  }

  if (object.slug === 'water-outlet') {
    removePressureVisualization(object);
  }
};

const objectMoveEndHandler = (object) => {
  if (isUndefined(object)) {
    return;
  }

  if (object?.slug !== 'water-outlet') {
    MontanaIrrigationAPI.waterSupply.update(MontanaIrrigationAPI.waterSupply.getMain(), -1);
  }
};

const postInit = async () => {
  await updateWaterOutletIcon(MontanaIrrigationAPI.waterSupply.getMain());

  const gardenObjects = MontanaAPI.objects.getGardenObjects();
  resolveDuplicateFabricIds(gardenObjects);

  document.getElementById('loading-animation').style.display = 'none';
  MontanaAPI.data.state.save(true);
};

const showAlert = (message) => {
  if (message === 'alerts.polygon.gras.locked') {
    MontanaEventBus.emit(MontanaEvents.alerts.displayInteractiveAlert, {
      content: message + '.message',
      title: message + '.title',
      id: 'gras-locked',
    });
  } else {
    MontanaEventBus.emit(MontanaEvents.alerts.displayAlert, message);
  }
};

const guiEvents = () => {
  MontanaEventBus.once(MontanaEvents.gui.gardenLoaded, postInit);

  MontanaEventBus.on(MontanaEvents.gui.resize, initResponsiveTools);
  MontanaEventBus.on(MontanaEvents.gui.alert, showAlert);
  MontanaEventBus.on(MontanaEvents.gui.mode.toggle, MontanaAPI.view.refreshScaling);
  MontanaEventBus.on(MontanaEvents.gui.centered, SprinklerTools.rescale);
  MontanaEventBus.on(MontanaEvents.gui.object.setMovingState, (state) => ReduxStore.dispatch(setObjectStates({ isObjectMoving: state })));

  MontanaEventBus.on(MontanaEvents.gui.objectMenu.toggle, (newMode) => ReduxStore.dispatch(setPlanMode(newMode)));
  MontanaEventBus.on(MontanaEvents.gui.objectMenu.toggleSubmenus, enableDisableSubmenus);
  MontanaEventBus.on(MontanaEvents.gui.objectMenu.triggerRefresh, () => {
    ReduxStore.dispatch(setForceUpdateMenu());
  });

  MontanaEventBus.on(MontanaEvents.gui.showIrrigationControllerModal, irrigationController.showModal);

  MontanaEventBus.on(MontanaEvents.gui.showIrrigationControllerModal, irrigationController.showModal);
};

const dataEvents = () => {
  MontanaEventBus.on(MontanaEvents.data.new, newGardenHandler);
  MontanaEventBus.on(MontanaEvents.data.save, montanaSave);
  MontanaEventBus.on(MontanaEvents.data.saveAs, montanaSaveGardenAs);

  MontanaEventBus.on(MontanaEvents.data.saveBeforeLogout, newGardenSaveBefore);
};

const canvasEvents = () => {
  MontanaEventBus.on(MontanaEvents.canvas.editStart, () => enableDisableSubmenus());
  MontanaEventBus.on(MontanaEvents.canvas.editEnd, () => enableDisableSubmenus(true));

  MontanaEventBus.on(MontanaEvents.canvas.object.added, dropWaterOutletHandler);
  MontanaEventBus.on(MontanaEvents.canvas.object.selected, objectSelectedHandler);
  MontanaEventBus.on(MontanaEvents.canvas.object.moveStart, objectMoveStartHandler);
  MontanaEventBus.on(MontanaEvents.canvas.object.moveEnd, objectMoveEndHandler);

  MontanaEventBus.on(MontanaEvents.canvas.zoom, () => {
    MontanaAPI.view.refreshScaling();

    switch (ReduxStore.state.gui.planMode) {
      case MontanaMode.Irrigation:
        SprinklerTools.rescale();
        initSprinklerStackIconObjects();
        break;
      case MontanaMode.EdgeIrrigation:
      case MontanaMode.WaterConnector:
        rescaleWaterConnectors();
        break;
    }

    if (ReduxStore.state.gui.planMode !== MontanaMode.Garden) {
      rescaleWaterSupply();
      updatePressureVisualization(MontanaIrrigationAPI.waterSupply.getMain());
    }

    ReduxStore.dispatch(setZoom(MontanaAPI.getCanvas().getZoom()));
  });

  MontanaEventBus.on(MontanaEvents.canvas.initialized, () => {
    MontanaAPI.getCanvas().on(CanvasEvent.object.added, (evt) => {
      if (evt.target.excludeFromExport) {
        return false;
      }
      ReduxStore.dispatch(setForceUpdateMenu());

      if (ReduxStore.state.gui.planMode === MontanaMode.Irrigation && evt.target.layer === Layer.Sprinklers) {
        initSprinklerStackIconObjects();
      }

      if (!ReduxStore.state.canvas.initializing) {
        if (evt.target.slug === slugIndex.WaterOutlet && MontanaAPI.objects.getBySlug(slugIndex.WaterOutlet) > 0) {
          MontanaAPI.objects.remove(evt.target);
        }
      }
    });

    registerPipeCanvasEvents(MontanaAPI.getCanvas());
  });
};

const gardenEvents = () => {
  MontanaEventBus.on(MontanaEvents.garden.new, () => {
    clearAllPipes();
    ReduxStore.dispatch(setControllerData({ delete: true }));
  });
};

const pipelineEvents = () => {
  // Pipeline event handlers
  MontanaEventBus.on(MontanaEvents.pipelines.finish, finishPipe);
  MontanaEventBus.on(MontanaEvents.pipelines.controller.move, moveNodeWithController);

  MontanaEventBus.on(MontanaEvents.gui.pipelines.reset, () => {
    drawAllPipes();
    ReduxStore.dispatch(setForceUpdateMenu());
  });
  MontanaEventBus.on(MontanaEvents.gui.pipelines.draw, drawPipe);
  MontanaEventBus.on(MontanaEvents.gui.pipelines.nodes.add, addNode);

  MontanaEventBus.on(MontanaEvents.pipelines.automation.start, executePipelineAutomation);
};

const waterSupplyEvents = () => {
  MontanaEventBus.on(MontanaEvents.waterSupply.update, ({ waterSupply, modificator }) =>
    MontanaIrrigationAPI.waterSupply.update(waterSupply, modificator)
  );
  MontanaEventBus.on(MontanaEvents.waterSupply.updatePressure, updatePressureVisualization);
  MontanaEventBus.on(MontanaEvents.waterSupply.removePressure, removePressureVisualization);
};

const waterConnectorEvents = () => {
  // Water connector event handlers
  MontanaEventBus.on(MontanaEvents.waterConnectors.incompatible, showModalDifferentWaterConnectorTypes);
  MontanaEventBus.on(MontanaEvents.waterConnectors.tooMany, showModalTooManyWaterConnectors);
};

const roboticMowerEvents = () => {
  MontanaEventBus.on(MontanaEvents.roboticMower.guideWire.intersections.add, addGuideWireIntersectionNotices);
  MontanaEventBus.on(MontanaEvents.roboticMower.guideWire.intersections.clear, clearGuideWireIntersectionNotices);
};

const initMontanaGUI = () => {
  regionSwitcherEvents();
  guiEvents();
  dataEvents();
  canvasEvents();
  pipelineEvents();
  gardenEvents();
  waterSupplyEvents();
  waterConnectorEvents();
  roboticMowerEvents();

  document.addEventListener(Runtime.handler.up, (event) => {
    if (!event?.target?.closest('.menu-level-n')) {
      ReduxStore.dispatch(setMenuData({ level2: null, level3: null }));
    }
  });

  // we need to init the current language before we can load the toolbar (which is then initialised in i18n)
  i18next.on('initialized', async () => {
    console.log('initialized');
    await UserApi.init();

    initReactComponents();

    const isGardenInitialized = MontanaAPI.getCanvas().reactComponent;

    if (!isGardenInitialized) {
      MontanaEventBus.once(MontanaEvents.canvas.initialized, async () => {
        initGardenPlanner();
        // init code that should be run after the canvas loaded all objects
        await initStartOverlay();
      });
      return;
    }

    await initGardenPlanner();
    // init code that should be run after the canvas loaded all objects
    await initStartOverlay();
  });
};

export default initMontanaGUI;
